import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static targets = [
    'smartCardForm',
    'smartCardInformations',
    'obrCardForm',
    'obrCardInformations',
    'discountedPrice',
    'soloPrice',
    'simplePrice',
    'choosenDocumentsLimit',
    'choosenDocumentsLimitValue',
    'choosenPackageKind',
    'PackagePrice',
    'docsMenu',
    'soloPackageDetails',
    'soloPackageToggle',
    'packageName',
    'packageDescription',
    'submitButton',
    'soloTogglePosition',
    'ksefEnabled',
    'isCopartnership',
  ];

  static activeDocumentLimitClassList = `x-active-documents-limit block w-full cursor-pointer select-none py-2 px-3
    text-start leading-5 transition-all hover:bg-primary-50/80
    hover:text-primary-600 flex items-center justify-start gap-3`;

  static inactiveDocumentLimitClassList = `block w-full cursor-pointer select-none py-2 pl-[34px] pr-3 text-start
    leading-5 transition-all hover:bg-primary-50/80 hover:text-primary-600`;

  static activeDocumentLimitIcon = `<span class="h-6 w-6 flex items-center justify-center icon-selected h-[10px]
    w-[10px] flex justify-center items-center text-primary-600"></span>`;

  ITEM_KEY_PACKAGE_KIND = 'package_kind';

  ITEM_VALUE_SOLO_PACKAGE = 'solo';

  ITEM_VALUE_SIMPLE_PACKAGE = 'simple';

  SOLO_BOX_ID = 'solo-accounting';

  SIMPLE_BOX_ID = 'invoices';

  connect() {
    this.displaySoloPackageDetails();
    this.checkFirstPackagesStateBySessionStorageOrSetDefaultPosition();
  }

  checkFirstPackagesStateBySessionStorageOrSetDefaultPosition() {
    if (!this.hasSoloPackageToggleTarget) return;

    const packageKind = sessionStorage.getItem(this.ITEM_KEY_PACKAGE_KIND) || this.soloTogglePositionTarget.value;

    if (packageKind) {
      this.soloPackageToggleTarget.checked = packageKind === this.ITEM_VALUE_SOLO_PACKAGE;
      this.toggleSoloKind();
    }
  }

  displaySoloPackageDetails() {
    if (this.hasSoloPackageToggleTarget && this.soloPackageToggleTarget.dataset.checked === 'true') {
      this.soloPackageToggleTarget.click();
    }
  }

  docsMenuTargetConnected() {
    this.docsMenuTarget.firstElementChild.classList = this.constructor.activeDocumentLimitClassList;
    this.docsMenuTarget.firstElementChild.insertAdjacentHTML('afterbegin', this.constructor.activeDocumentLimitIcon);
  }

  toggleSmartForm() {
    this.smartCardFormTarget.classList.toggle('hidden');
    this.smartCardInformationsTargets.forEach((element) => {
      element.classList.toggle('hidden');
    });
  }

  toggleObrForm() {
    this.obrCardFormTarget.classList.toggle('hidden');
    this.obrCardInformationsTargets.forEach((element) => {
      element.classList.toggle('hidden');
    });
  }

  refreshPackagePrice() {
    const packageKind = this.choosenPackageKindTarget.value;
    const packagePrice = this.choosenDocumentsLimitValueTarget.dataset[packageKind];
    this.PackagePriceTarget.textContent = `${packagePrice},00`;
  }

  selectDocumentLimit(event) {
    const activeDocumentLimitElement = document.querySelector('.x-active-documents-limit');
    const selectedDocumentLimitElement = event.target;

    this.choosenDocumentsLimitTarget.textContent = selectedDocumentLimitElement.dataset.name;
    this.choosenDocumentsLimitValueTarget.value = selectedDocumentLimitElement.value;

    if (activeDocumentLimitElement) {
      activeDocumentLimitElement.firstElementChild.remove();
      activeDocumentLimitElement.classList = this.constructor.inactiveDocumentLimitClassList;
    }

    selectedDocumentLimitElement.classList = this.constructor.activeDocumentLimitClassList;
    selectedDocumentLimitElement.insertAdjacentHTML('afterbegin', this.constructor.activeDocumentLimitIcon);

    ['basic', 'optimum', 'premium'].forEach((kind) => {
      this.choosenDocumentsLimitValueTarget.dataset[kind] = selectedDocumentLimitElement.getAttribute(`data-${kind}`);
    });

    this.refreshPackagePrice();

    const dropdown = selectedDocumentLimitElement.parentElement;
    dropdown.classList.remove('block');
    dropdown.classList.add('hidden');
  }

  switchPackageKind(event) {
    const checkboxOptimum = document.querySelector('.x-check-optimum');
    const checkboxPremium = document.querySelector('.x-check-premium');
    let detectedKind;

    if (event.target === checkboxPremium) {
      if (checkboxPremium.checked) {
        checkboxOptimum.checked = true;
        detectedKind = 'premium';
      } else {
        detectedKind = 'optimum';
      }
    } else if (checkboxOptimum.checked && event.target === checkboxOptimum) {
      detectedKind = 'optimum';
    } else {
      checkboxPremium.checked = false;
      checkboxOptimum.checked = false;
      detectedKind = 'basic';
    }

    this.choosenPackageKindTarget.value = detectedKind;
    this.refreshPackagePrice();
  }

  toggleSoloKind() {
    if (!this.hasSoloPackageToggleTarget) return;

    const ksefEnabled = this.hasKsefEnabledTarget && this.ksefEnabledTarget.value === 'true';
    const isCopartnership = this.hasIsCopartnershipTarget && this.isCopartnershipTarget.value === 'true';
    const packageKind = this.soloPackageToggleTarget.checked
      ? this.ITEM_VALUE_SOLO_PACKAGE
      : this.ITEM_VALUE_SIMPLE_PACKAGE;
    const hiddenClassName = 'hidden';
    const discountedPrice = this.discountedPriceTarget.value;
    const soloPrice = this.hasSoloPriceTarget ? this.soloPriceTarget.value : '29,99';
    const simplePrice = this.simplePriceTarget.value;
    const defaultSimpleDescription = ksefEnabled
      ? 'Dla wszystkich typów firm, które szukają kompleksowego narzędzia do obsługi faktur, bez księgowości.'
      : 'Dla klientów, którzy chcą wystawiać tylko faktury w aplikacji inFakt.';
    const defaultSoloDescription = isCopartnership
      ? 'Dla spółek, które chcą samodzielnie prowadzić księgowość.'
      : 'Dla klientów, którzy mają jednoosobową działalność gospodarczą i chcą samodzielnie prowadzić księgowość.';
    const simpleDescription = this.packageDescriptionTarget.dataset.simpleDescription || defaultSimpleDescription;
    const soloDescription = this.packageDescriptionTarget.dataset.soloDescription || defaultSoloDescription;
    const packageData = {
      solo: {
        price: discountedPrice || soloPrice,
        name: 'Samodzielna księgowość',
        description: soloDescription,
      },
      simple: {
        price: simplePrice,
        name: 'inFakt Faktury',
        description: simpleDescription,
      },
    };
    const currentPackageData = packageData[packageKind];
    this.PackagePriceTarget.textContent = currentPackageData.price;
    this.packageNameTarget.textContent = currentPackageData.name;
    this.packageDescriptionTarget.textContent = currentPackageData.description;
    this.choosenPackageKindTarget.value = packageKind;
    this.updateBoxId(packageKind);
    this.updateBoxTestIds(packageKind);
    // this.soloPackageDetailsTarget.classList.toggle('hidden');

    if (packageKind === 'simple') {
      this.soloPackageDetailsTarget.classList.add(hiddenClassName);
    }

    if (packageKind === 'solo') {
      this.soloPackageDetailsTarget.classList.remove(hiddenClassName);
    }

    this.updateSubmitButton();
    sessionStorage.setItem(this.ITEM_KEY_PACKAGE_KIND, packageKind);
  }

  updateBoxId(packageKind) {
    if (![this.SIMPLE_BOX_ID, this.SOLO_BOX_ID].includes(this.element.id)) return;

    this.element.id = packageKind === this.ITEM_VALUE_SOLO_PACKAGE ? this.SOLO_BOX_ID : this.SIMPLE_BOX_ID;
  }

  updateBoxTestIds(packageKind) {
    const previousKind =
      packageKind === this.ITEM_VALUE_SOLO_PACKAGE ? this.ITEM_VALUE_SIMPLE_PACKAGE : this.ITEM_VALUE_SOLO_PACKAGE;

    this.element.querySelectorAll(`[data-testid^="pricing-box-${previousKind}-"]`).forEach((element) => {
      element.dataset.testid = element.dataset.testid.replace(
        `pricing-box-${previousKind}-`,
        `pricing-box-${packageKind}-`,
      );
    });
  }

  updateSubmitButton() {
    if (this.hasSubmitButtonTarget) {
      const soloToggleOn = this.soloPackageToggleTarget.checked;
      const soloPlanActive = this.submitButtonTarget.dataset.soloPlanActive === 'true';
      this.submitButtonTarget.disabled = !soloToggleOn || soloPlanActive;
      if (soloPlanActive) {
        this.submitButtonTarget.textContent = soloToggleOn ? 'Aktualny plan' : 'Posiadasz wyższy plan';
      } else {
        this.submitButtonTarget.textContent = soloToggleOn ? 'Kup teraz' : 'Aktualny plan';
      }
    }
  }

  disableSubmitButton(event) {
    const form = event.target;
    const submitButton = form.querySelector('button[type="submit"]');
    if (submitButton) {
      submitButton.disabled = true;
    }
  }
}
